import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

/**
 * ImageUploader — small drop-in image upload + URL field.
 * Used in MyStoreTab, MyCoursesTab, and any place a member needs to upload one image.
 *
 * Props:
 *   value     — current image URL
 *   onChange  — fires with new URL after upload, or "" on remove
 *   testid    — data-testid prefix
 *   label     — button text (default "Upload Image")
 */
export default function ImageUploader({ value, onChange, testid = "image", label = "Upload Image" }) {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!(f.type || "").startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Image too large (max 15MB). Compress it at tinypng.com and try again."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider-cap cursor-pointer text-foreground/80 hover:text-red-wine border-2 border-foreground/20 hover:border-[#F39A1F] rounded-md px-3 py-2 hover:bg-white/60 transition" data-testid={`${testid}-upload-btn`}>
          <Upload className="w-3.5 h-3.5"/> {busy ? "Uploading…" : label}
          <input type="file" accept="image/*" onChange={handle} className="hidden" disabled={busy} data-testid={`${testid}-upload-input`}/>
        </label>
        <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="...or paste an image URL" className="flex-1 min-w-[200px] px-3 py-2 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-xs font-mono" data-testid={`${testid}-url-input`}/>
      </div>
      {value && (
        <div className="relative inline-block">
          <img src={value} alt="" className="rounded-md max-h-24 border-2 border-foreground/10"/>
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-[#2E9F3A] text-white rounded-full p-1 shadow hover:bg-[#1F8A2A]" data-testid={`${testid}-remove-btn`}><X className="w-3 h-3"/></button>
        </div>
      )}
    </div>
  );
}
